/**
 * Partners transport boundary — parties, profiles, addresses, contacts,
 * groups, payment terms and channel identities.
 *
 * Every request/response alias comes from the generated canonical OpenAPI
 * client; nothing here hand-writes a wire type. The app's HTTP layer owns
 * credentials, idempotency keys and ETag headers — this module binds those
 * mechanics to the 40 partners operations the ratified contract publishes.
 *
 * Concurrency policy, verified against openapi/generated/route_policy.gen.go:
 *   - POST /parties, /parties/{id}/addresses, /parties/{id}/contacts,
 *     /customer-groups, /supplier-groups, /payment-terms,
 *     /parties/{id}/archive and /parties/{id}/unarchive are `client-key`
 *     idempotent and MUST carry an Idempotency-Key.
 *   - Every PATCH and PUT takes a REQUIRED strong If-Match; archive takes one
 *     too (optional only for the `dry_run=true` preflight).
 *   - DELETE archives the row and takes neither.
 */
import type { components, operations } from "@erp/api-client";

import {
  apiFetchOperational as apiFetch,
  apiRequestOperational as apiRequest,
  type Page,
} from "@/lib/api/http";
import { collectCursorPages, fetchWithEtag, type WithEtag } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

const MERGE_PATCH = { "Content-Type": "application/merge-patch+json" } as const;

// ── Enums and scalars ────────────────────────────────────────────────────

export type PartyKind = Schemas["PartnersPartyKind"];
export type PartyStatus = Schemas["PartnersPartyStatus"];
export type PartyRole = Schemas["PartnersPartyRole"];
export type LifecycleStatus = Schemas["PartnersLifecycleStatus"];
export type AddressType = Schemas["PartnersAddressType"];
export type CountryCode = Schemas["PartnersCountryCode"];
export type MatchedBy = Schemas["PartnersChannelIdentityMatchedBy"];
export type ArchiveReasonCode = Schemas["PartnersPartyArchiveReasonCode"];

export const PARTY_KINDS = ["person", "org"] as const satisfies readonly PartyKind[];
export const PARTY_STATUSES = [
  "active",
  "archived",
  "pseudonymized",
] as const satisfies readonly PartyStatus[];
export const PARTY_ROLES = ["customer", "supplier"] as const satisfies readonly PartyRole[];
export const LIFECYCLE_STATUSES = [
  "active",
  "archived",
] as const satisfies readonly LifecycleStatus[];
export const ADDRESS_TYPES = [
  "billing",
  "shipping",
  "both",
] as const satisfies readonly AddressType[];
export const MATCHED_BY_VALUES = [
  "channel_identity",
  "email",
  "created",
] as const satisfies readonly MatchedBy[];
export const ARCHIVE_REASON_CODES = [
  "duplicate_record",
  "relationship_ended",
  "operational_inactive",
  "compliance_block",
] as const satisfies readonly ArchiveReasonCode[];

// ── Entities ─────────────────────────────────────────────────────────────

export type Party = Schemas["PartnersPartySummary"];
export type PartyDetail = Schemas["PartnersPartyDetail"];
export type PartyCreate = Schemas["PartnersPartyCreate"];
export type PartyPatch = Schemas["PartnersPartyPatch"];
export type TaxRegistration = Schemas["PartnersTaxRegistration"];
export type PartyArchiveResult = Schemas["PartnersPartyArchiveResult"];
export type PartyArchiveBlocker = Schemas["PartnersPartyArchiveBlocker"];
export type CustomerProfile = Schemas["PartnersCustomerProfile"];
export type CustomerProfilePut = Schemas["PartnersCustomerProfilePut"];
export type CustomerProfilePatch = Schemas["PartnersCustomerProfilePatch"];
export type SupplierProfile = Schemas["PartnersSupplierProfile"];
export type SupplierProfilePut = Schemas["PartnersSupplierProfilePut"];
export type SupplierProfilePatch = Schemas["PartnersSupplierProfilePatch"];
export type PartyAddress = Schemas["PartnersPartyAddress"];
export type PartyAddressCreate = Schemas["PartnersPartyAddressCreate"];
export type PartyAddressPatch = Schemas["PartnersPartyAddressPatch"];
export type PartyContact = Schemas["PartnersPartyContact"];
export type PartyContactCreate = Schemas["PartnersPartyContactCreate"];
export type PartyContactPatch = Schemas["PartnersPartyContactPatch"];
export type ChannelIdentity = Schemas["PartnersChannelIdentity"];
export type CustomerGroup = Schemas["PartnersCustomerGroup"];
export type CustomerGroupCreate = Schemas["PartnersCustomerGroupCreate"];
export type CustomerGroupPatch = Schemas["PartnersCustomerGroupPatch"];
export type SupplierGroup = Schemas["PartnersSupplierGroup"];
export type SupplierGroupCreate = Schemas["PartnersSupplierGroupCreate"];
export type SupplierGroupPatch = Schemas["PartnersSupplierGroupPatch"];
export type PaymentTerm = Schemas["PartnersPaymentTerm"];
export type PaymentTermCreate = Schemas["PartnersPaymentTermCreate"];
export type PaymentTermPatch = Schemas["PartnersPaymentTermPatch"];

// ── Query parameters ─────────────────────────────────────────────────────

export type PartyListQuery = NonNullable<operations["partnersListParties"]["parameters"]["query"]>;
export type AddressListQuery = NonNullable<
  operations["partnersListPartyAddresses"]["parameters"]["query"]
>;
export type ContactListQuery = NonNullable<
  operations["partnersListPartyContacts"]["parameters"]["query"]
>;
export type GroupListQuery = NonNullable<
  operations["partnersListCustomerGroups"]["parameters"]["query"]
>;
export type PaymentTermListQuery = NonNullable<
  operations["partnersListPaymentTerms"]["parameters"]["query"]
>;

type PartyPage = operations["partnersListParties"]["responses"][200]["content"]["application/json"];
type AddressPage =
  operations["partnersListPartyAddresses"]["responses"][200]["content"]["application/json"];
type ContactPage =
  operations["partnersListPartyContacts"]["responses"][200]["content"]["application/json"];
type ChannelIdentityPage =
  operations["partnersListPartyChannelIdentities"]["responses"][200]["content"]["application/json"];
type CustomerGroupPage =
  operations["partnersListCustomerGroups"]["responses"][200]["content"]["application/json"];
type SupplierGroupPage =
  operations["partnersListSupplierGroups"]["responses"][200]["content"]["application/json"];
type PaymentTermPage =
  operations["partnersListPaymentTerms"]["responses"][200]["content"]["application/json"];

type GeneratedPage<T> = Schemas["PageEnvelope"] & { readonly items: readonly T[] };

function toMutablePage<T>(page: GeneratedPage<T>): Page<T> {
  return { items: [...page.items], next_cursor: page.next_cursor };
}

const path = (segment: string): string => encodeURIComponent(segment);

// ── Parties ──────────────────────────────────────────────────────────────

/** `partnersListParties` — GET /parties · partners.party.read. */
export async function listParties(
  query: PartyListQuery,
  signal?: AbortSignal,
): Promise<Page<Party>> {
  return toMutablePage(await apiFetch<PartyPage>("/parties", { query: { ...query }, signal }));
}

/** `partnersCreateParty` — POST /parties · partners.party.create · client-key. */
export async function createParty(
  input: PartyCreate,
  idempotencyKey: string,
): Promise<WithEtag<Party>> {
  const { data, etag } = await apiRequest<Party>("/parties", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
  return { data, etag };
}

/**
 * `partnersGetParty` — GET /parties/{partyId} · partners.party.pii.read.
 * This is the audited restricted PII read: it is the only operation that
 * returns `tax_registration`, so the detail screen sits behind that grant.
 */
export function getParty(partyId: string): Promise<WithEtag<PartyDetail>> {
  return fetchWithEtag<PartyDetail>(`/parties/${path(partyId)}`);
}

/** `partnersUpdateParty` — PATCH /parties/{partyId} · partners.party.update · If-Match. */
export async function updateParty(
  partyId: string,
  patch: PartyPatch,
  ifMatch: string,
): Promise<WithEtag<Party>> {
  const { data, etag } = await apiRequest<Party>(`/parties/${path(partyId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

/**
 * `partnersArchiveParty` (preflight) — POST /parties/{partyId}/archive?dry_run=true.
 * The contract forbids both a body and If-Match on a dry run; a blocked
 * preflight answers 422 `party_archive_blocked` with the full blocker list.
 */
export async function dryRunArchiveParty(
  partyId: string,
  idempotencyKey: string,
): Promise<PartyArchiveResult> {
  return apiFetch<PartyArchiveResult>(`/parties/${path(partyId)}/archive`, {
    method: "POST",
    query: { dry_run: true },
    idempotencyKey,
  });
}

/** `partnersArchiveParty` — POST /parties/{partyId}/archive · partners.party.archive. */
export async function archiveParty(
  partyId: string,
  reasonCode: ArchiveReasonCode,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<PartyArchiveResult>> {
  const { data, etag } = await apiRequest<PartyArchiveResult>(`/parties/${path(partyId)}/archive`, {
    method: "POST",
    body: { archive_reason_code: reasonCode } satisfies Schemas["PartnersPartyArchiveRequest"],
    ifMatch,
    idempotencyKey,
  });
  return { data, etag };
}

/** `partnersUnarchiveParty` — POST /parties/{partyId}/unarchive · partners.party.archive. */
export async function unarchiveParty(
  partyId: string,
  ifMatch: string,
  idempotencyKey: string,
): Promise<WithEtag<Party>> {
  const { data, etag } = await apiRequest<Party>(`/parties/${path(partyId)}/unarchive`, {
    method: "POST",
    ifMatch,
    idempotencyKey,
  });
  return { data, etag };
}

/** `partnersListPartyChannelIdentities` — GET · partners.channel_identity.read. */
export async function listChannelIdentities(
  partyId: string,
  signal?: AbortSignal,
): Promise<ChannelIdentity[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<ChannelIdentityPage>(`/parties/${path(partyId)}/channel-identities`, {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

// ── Customer profile ─────────────────────────────────────────────────────

/** `partnersGetCustomerProfile` — GET · partners.customer_profile.read. */
export function getCustomerProfile(partyId: string): Promise<WithEtag<CustomerProfile>> {
  return fetchWithEtag<CustomerProfile>(`/parties/${path(partyId)}/customer-profile`);
}

/**
 * `partnersPutCustomerProfile` — PUT · partners.customer_profile.update.
 * Create-or-replace. On first enable the If-Match validator is the strong
 * party aggregate ETag from GET /parties/{partyId}.
 */
export async function putCustomerProfile(
  partyId: string,
  input: CustomerProfilePut,
  ifMatch: string,
): Promise<WithEtag<CustomerProfile>> {
  const { data, etag } = await apiRequest<CustomerProfile>(
    `/parties/${path(partyId)}/customer-profile`,
    { method: "PUT", body: input, ifMatch },
  );
  return { data, etag };
}

/** `partnersUpdateCustomerProfile` — PATCH · partners.customer_profile.update. */
export async function updateCustomerProfile(
  partyId: string,
  patch: CustomerProfilePatch,
  ifMatch: string,
): Promise<WithEtag<CustomerProfile>> {
  const { data, etag } = await apiRequest<CustomerProfile>(
    `/parties/${path(partyId)}/customer-profile`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

/** `partnersArchiveCustomerProfile` — DELETE · partners.customer_profile.archive. */
export function archiveCustomerProfile(partyId: string): Promise<void> {
  return apiFetch<void>(`/parties/${path(partyId)}/customer-profile`, { method: "DELETE" });
}

// ── Supplier profile ─────────────────────────────────────────────────────

/** `partnersGetSupplierProfile` — GET · partners.supplier_profile.read. */
export function getSupplierProfile(partyId: string): Promise<WithEtag<SupplierProfile>> {
  return fetchWithEtag<SupplierProfile>(`/parties/${path(partyId)}/supplier-profile`);
}

/** `partnersPutSupplierProfile` — PUT · partners.supplier_profile.update. */
export async function putSupplierProfile(
  partyId: string,
  input: SupplierProfilePut,
  ifMatch: string,
): Promise<WithEtag<SupplierProfile>> {
  const { data, etag } = await apiRequest<SupplierProfile>(
    `/parties/${path(partyId)}/supplier-profile`,
    { method: "PUT", body: input, ifMatch },
  );
  return { data, etag };
}

/** `partnersUpdateSupplierProfile` — PATCH · partners.supplier_profile.update. */
export async function updateSupplierProfile(
  partyId: string,
  patch: SupplierProfilePatch,
  ifMatch: string,
): Promise<WithEtag<SupplierProfile>> {
  const { data, etag } = await apiRequest<SupplierProfile>(
    `/parties/${path(partyId)}/supplier-profile`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

/** `partnersArchiveSupplierProfile` — DELETE · partners.supplier_profile.archive. */
export function archiveSupplierProfile(partyId: string): Promise<void> {
  return apiFetch<void>(`/parties/${path(partyId)}/supplier-profile`, { method: "DELETE" });
}

// ── Addresses ────────────────────────────────────────────────────────────

/** `partnersListPartyAddresses` — GET · partners.party_address.read. */
export async function listPartyAddresses(
  partyId: string,
  query: AddressListQuery = {},
  signal?: AbortSignal,
): Promise<PartyAddress[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<AddressPage>(`/parties/${path(partyId)}/addresses`, {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** `partnersGetPartyAddress` — GET · partners.party_address.read. */
export function getPartyAddress(
  partyId: string,
  addressId: string,
): Promise<WithEtag<PartyAddress>> {
  return fetchWithEtag<PartyAddress>(`/parties/${path(partyId)}/addresses/${path(addressId)}`);
}

/** `partnersCreatePartyAddress` — POST · partners.party_address.create · client-key. */
export function createPartyAddress(
  partyId: string,
  input: PartyAddressCreate,
  idempotencyKey: string,
): Promise<PartyAddress> {
  return apiFetch<PartyAddress>(`/parties/${path(partyId)}/addresses`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/**
 * `partnersUpdatePartyAddress` — PATCH · partners.party_address.update.
 * `is_default: true` clears the prior default for every overlapping purpose
 * server-side; the UI reconciles from the response, never from local guesses.
 */
export async function updatePartyAddress(
  partyId: string,
  addressId: string,
  patch: PartyAddressPatch,
  ifMatch: string,
): Promise<WithEtag<PartyAddress>> {
  const { data, etag } = await apiRequest<PartyAddress>(
    `/parties/${path(partyId)}/addresses/${path(addressId)}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

/** `partnersArchivePartyAddress` — DELETE · partners.party_address.archive. */
export function archivePartyAddress(partyId: string, addressId: string): Promise<void> {
  return apiFetch<void>(`/parties/${path(partyId)}/addresses/${path(addressId)}`, {
    method: "DELETE",
  });
}

// ── Contacts ─────────────────────────────────────────────────────────────

/** `partnersListPartyContacts` — GET · partners.party_contact.read. */
export async function listPartyContacts(
  partyId: string,
  query: ContactListQuery = {},
  signal?: AbortSignal,
): Promise<PartyContact[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<ContactPage>(`/parties/${path(partyId)}/contacts`, {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** `partnersGetPartyContact` — GET · partners.party_contact.read. */
export function getPartyContact(
  partyId: string,
  contactId: string,
): Promise<WithEtag<PartyContact>> {
  return fetchWithEtag<PartyContact>(`/parties/${path(partyId)}/contacts/${path(contactId)}`);
}

/** `partnersCreatePartyContact` — POST · partners.party_contact.create · client-key. */
export function createPartyContact(
  partyId: string,
  input: PartyContactCreate,
  idempotencyKey: string,
): Promise<PartyContact> {
  return apiFetch<PartyContact>(`/parties/${path(partyId)}/contacts`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/** `partnersUpdatePartyContact` — PATCH · partners.party_contact.update. */
export async function updatePartyContact(
  partyId: string,
  contactId: string,
  patch: PartyContactPatch,
  ifMatch: string,
): Promise<WithEtag<PartyContact>> {
  const { data, etag } = await apiRequest<PartyContact>(
    `/parties/${path(partyId)}/contacts/${path(contactId)}`,
    { method: "PATCH", body: patch, ifMatch, headers: { ...MERGE_PATCH } },
  );
  return { data, etag };
}

/** `partnersArchivePartyContact` — DELETE · partners.party_contact.archive. */
export function archivePartyContact(partyId: string, contactId: string): Promise<void> {
  return apiFetch<void>(`/parties/${path(partyId)}/contacts/${path(contactId)}`, {
    method: "DELETE",
  });
}

// ── Customer groups ──────────────────────────────────────────────────────

/** `partnersListCustomerGroups` — GET · partners.customer_group.read. */
export async function listCustomerGroups(
  query: GroupListQuery = {},
  signal?: AbortSignal,
): Promise<CustomerGroup[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<CustomerGroupPage>("/customer-groups", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** `partnersGetCustomerGroup` — GET · partners.customer_group.read. */
export function getCustomerGroup(groupId: string): Promise<WithEtag<CustomerGroup>> {
  return fetchWithEtag<CustomerGroup>(`/customer-groups/${path(groupId)}`);
}

/** `partnersCreateCustomerGroup` — POST · partners.customer_group.create · client-key. */
export function createCustomerGroup(
  input: CustomerGroupCreate,
  idempotencyKey: string,
): Promise<CustomerGroup> {
  return apiFetch<CustomerGroup>("/customer-groups", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/** `partnersUpdateCustomerGroup` — PATCH · partners.customer_group.update. */
export async function updateCustomerGroup(
  groupId: string,
  patch: CustomerGroupPatch,
  ifMatch: string,
): Promise<WithEtag<CustomerGroup>> {
  const { data, etag } = await apiRequest<CustomerGroup>(`/customer-groups/${path(groupId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

/** `partnersArchiveCustomerGroup` — DELETE · partners.customer_group.archive. */
export function archiveCustomerGroup(groupId: string): Promise<void> {
  return apiFetch<void>(`/customer-groups/${path(groupId)}`, { method: "DELETE" });
}

// ── Supplier groups ──────────────────────────────────────────────────────

/** `partnersListSupplierGroups` — GET · partners.supplier_group.read. */
export async function listSupplierGroups(
  query: GroupListQuery = {},
  signal?: AbortSignal,
): Promise<SupplierGroup[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<SupplierGroupPage>("/supplier-groups", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** `partnersGetSupplierGroup` — GET · partners.supplier_group.read. */
export function getSupplierGroup(groupId: string): Promise<WithEtag<SupplierGroup>> {
  return fetchWithEtag<SupplierGroup>(`/supplier-groups/${path(groupId)}`);
}

/** `partnersCreateSupplierGroup` — POST · partners.supplier_group.create · client-key. */
export function createSupplierGroup(
  input: SupplierGroupCreate,
  idempotencyKey: string,
): Promise<SupplierGroup> {
  return apiFetch<SupplierGroup>("/supplier-groups", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/** `partnersUpdateSupplierGroup` — PATCH · partners.supplier_group.update. */
export async function updateSupplierGroup(
  groupId: string,
  patch: SupplierGroupPatch,
  ifMatch: string,
): Promise<WithEtag<SupplierGroup>> {
  const { data, etag } = await apiRequest<SupplierGroup>(`/supplier-groups/${path(groupId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

/** `partnersArchiveSupplierGroup` — DELETE · partners.supplier_group.archive. */
export function archiveSupplierGroup(groupId: string): Promise<void> {
  return apiFetch<void>(`/supplier-groups/${path(groupId)}`, { method: "DELETE" });
}

// ── Payment terms ────────────────────────────────────────────────────────

/** `partnersListPaymentTerms` — GET · partners.payment_terms.read. */
export async function listPaymentTerms(
  query: PaymentTermListQuery = {},
  signal?: AbortSignal,
): Promise<PaymentTerm[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<PaymentTermPage>("/payment-terms", {
        query: { ...query, limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** `partnersGetPaymentTerm` — GET · partners.payment_terms.read. */
export function getPaymentTerm(paymentTermId: string): Promise<WithEtag<PaymentTerm>> {
  return fetchWithEtag<PaymentTerm>(`/payment-terms/${path(paymentTermId)}`);
}

/** `partnersCreatePaymentTerm` — POST · partners.payment_terms.create · client-key. */
export function createPaymentTerm(
  input: PaymentTermCreate,
  idempotencyKey: string,
): Promise<PaymentTerm> {
  return apiFetch<PaymentTerm>("/payment-terms", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/** `partnersUpdatePaymentTerm` — PATCH · partners.payment_terms.update. */
export async function updatePaymentTerm(
  paymentTermId: string,
  patch: PaymentTermPatch,
  ifMatch: string,
): Promise<WithEtag<PaymentTerm>> {
  const { data, etag } = await apiRequest<PaymentTerm>(`/payment-terms/${path(paymentTermId)}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { ...MERGE_PATCH },
  });
  return { data, etag };
}

/** `partnersArchivePaymentTerm` — DELETE · partners.payment_terms.archive. */
export function archivePaymentTerm(paymentTermId: string): Promise<void> {
  return apiFetch<void>(`/payment-terms/${path(paymentTermId)}`, { method: "DELETE" });
}
